import { Box, Container, Flex, Grid, Heading, Text, useMediaQuery } from '@chakra-ui/react';
import { ButtonFullWidthMobile } from '@components/Button';
import {
  APP_ROUTES_ABSOLUTE,
  AppLink,
  GradientShadowOverlay,
  Image,
  JungleAdventureSidebar,
  LANDING_ROUTES,
  pxToRem,
  SHARED_ROUTES,
  TRANSITIONS,
  useWeb3Context,
} from '@cyberkongz/ui';
import {
  SliderNavigationRectangle,
  SliderNavigationRounded,
  SliderWrapper,
} from '@cyberkongz/ui-slider';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useState } from 'react';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';
import { useAccount } from 'wagmi';

function SLIDER_ITEMS(): {
  id: JungleAdventureSidebar | 'adventure_run' | 'bridge';
  imgPrefix?: string;
  title: string;
  description: ReactNode;
}[] {
  return [
    {
      id: 'adventure_run',
      title: 'PLAY & KOLLECT',
      description: (
        <>
          Play & Kollect is the ultimate on-chain web3 gaming experience, exclusively on Ronin.
          <Text mt={2}>
            Send your CyberKongz VX on exciting adventure runs, immerse in special events, and
            embark on epic encounters with enemies, all whilst collecting rewards and prizes.
          </Text>
          <Text mt={2}>
            Learn more about Play & Kollect on our{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={LANDING_ROUTES.INTERACTIVE_ROADMAP}
            >
              Interactive Roadmap
            </AppLink>{' '}
            and{' '}
            <AppLink css={{ textDecoration: 'underline' }} href={SHARED_ROUTES.DOCS_PATH('/')}>
              Gitbook
            </AppLink>
            .
          </Text>
        </>
      ),
    },
    {
      id: 'bridge',
      title: 'Ronin Bridge',
      description: (
        <>
          CyberKongz VX and $BANANA will need to be bridged from Ethereum Mainnet to Ronin in order
          to participate in our web3 gaming experience: Play & Kollect.
        </>
      ),
    },
    {
      id: 'ms-kongz',
      imgPrefix: 'ms_kongz',
      title: 'ms kongz',
      description: (
        <>
          Ms Kongz is the management hub for our web3 gaming experience where players can equip gear
          to boost Gear Score and heal their VX with Medipacks when HP is low.
          <Text mt={2}>
            Learn more about{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={SHARED_ROUTES.DOCS_PATH('/play/gear-score-gs')}
            >
              Gear
            </AppLink>{' '}
            and{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={SHARED_ROUTES.DOCS_PATH('/play/health-points-hp')}
            >
              HP
            </AppLink>{' '}
            in our{' '}
            <AppLink css={{ textDecoration: 'underline' }} href={SHARED_ROUTES.DOCS_PATH('/')}>
              Gitbook
            </AppLink>
            .
          </Text>
        </>
      ),
    },
    {
      id: 'portal',
      title: 'ADVENTURE PORTAL',
      description: (
        <>
          Players can send CyberKongz VX&apos;s on jungle Adventure Runs to gather Kongium, collect
          loot, and win prizes. Adventure Runs require Kredits and are split into 4 tiers, the
          higher the tier the better the potential rewards.
          <Text mt={2}>Higher tiers can be unlocked with Gear Score and spending $RON.</Text>
          <Text mt={2}>
            Learn more about Adventure Runs in our{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={SHARED_ROUTES.DOCS_PATH('/play/adventure-runs')}
            >
              Gitbook
            </AppLink>
            .
          </Text>
        </>
      ),
    },
    {
      id: 'temple',
      title: 'temple of rising',
      description: (
        <>
          Rise through the ranks in the Temple of Rising! Players can spend their Kongium and EXP to
          level up at the Temple of Rising. Levelling up is important for increasing players chances
          of collecting better loot.
          <Text mt={2}>
            Learn more about Levels and XP in our{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={SHARED_ROUTES.DOCS_PATH('/play/experience-and-levels')}
            >
              Gitbook
            </AppLink>
            .
          </Text>
        </>
      ),
    },
    {
      id: 'treasury',
      title: 'treasury',
      description: (
        <>
          The Treasury is where players can find our web3 gaming prize pools. Players can commit
          Kongium and redeem prizes!
          <Text mt={2}>
            Each season starts with 10,000 $BANANA in the Kongium Prize Pool. The total Prize Pool
            increases as Kredits are spent on Adventure Runs throughout the season.
          </Text>
          <Text mt={2}>
            In addition, each season there will be a Jackpot Prize Pool to win a share of $BANANA.
            Each VX that spends a minimum of 30,000 Kredits will be entered into a raffle to make
            the Lucky 100. A random draw will be taken amongst the Lucky 100 to determine placement.
          </Text>
          <Text mt={2}>
            Learn more about Prize Pools in our{' '}
            <AppLink
              css={{ textDecoration: 'underline' }}
              href={SHARED_ROUTES.DOCS_PATH('/kollect/usdbanana-prize-pool')}
            >
              Gitbook
            </AppLink>
            .
          </Text>
        </>
      ),
    },
  ];
}

function ActiveSlideContent({
  activeSlide,
}: {
  activeSlide: ReturnType<typeof SLIDER_ITEMS>[number];
}) {
  const { isConnected } = useAccount();
  const { openConnectWalletModal } = useWeb3Context();
  const { title, description, id } = activeSlide;

  return (
    <Grid
      css={{
        gap: [8, 4],
        gridTemplateColumns: ['1fr', '1fr 400px'],
        minHeight: ['none', pxToRem(595)],
      }}
    >
      <Box css={{ maxWidth: pxToRem(700), width: '100%' }}>
        <motion.div
          key={activeSlide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Heading variant="displaySecondary" mb={2} mt={[pxToRem(200), 0]}>
            {title}
          </Heading>
          <Box
            css={{
              fontSize: ['md', 'lg'],
              maxWidth: '33em',
              color: 'textSecondary',
              mb: [4, 5],
              minHeight: '6em',
            }}
          >
            {description}
          </Box>
        </motion.div>
        <ButtonFullWidthMobile
          href={
            isConnected
              ? id === 'adventure_run'
                ? `${APP_ROUTES_ABSOLUTE.HOME}/jungle-adventure`
                : id === 'bridge'
                ? SHARED_ROUTES.BRIDGE
                : APP_ROUTES_ABSOLUTE.JUNGLE_ADVENTURE_SIDEBAR(id)
              : undefined
          }
          onClick={isConnected ? undefined : () => openConnectWalletModal()}
          variant="displayOutline"
        >
          {isConnected ? `Go to ${title}` : 'Connect Wallet'}
        </ButtonFullWidthMobile>
      </Box>
    </Grid>
  );
}

export function SliderSection() {
  const [isMobile] = useMediaQuery(['(max-width: 767px)'], {
    fallback: [false],
  });
  const [swiper, setSwiper] = useState<Swiper>();
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderItems = SLIDER_ITEMS().concat(SLIDER_ITEMS()); // Workaround for slider loop not working in react-swiper > v8 when there are too few slides

  return (
    <AnimatePresence>
      <Box css={{ position: 'relative', pt: [0, pxToRem(120)], pb: [10, pxToRem(120)] }}>
        <Container
          css={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            pb: [0, pxToRem(200)],
          }}
        >
          <Heading css={{ display: ['block', 'none'], order: -2, mb: 4 }}>P&K MAP</Heading>
          <ActiveSlideContent {...{ activeSlide: sliderItems[activeSlide] }} />
          <Grid
            css={{
              gap: 4,
              gridTemplateColumns: ['1fr', '1fr 400px'],
              order: [-1, 1],
              position: ['relative', 'absolute'],
              bottom: [0, pxToRem(70)],
              transform: ['none', `translateX(${pxToRem(-16)})`],
            }}
          >
            <Box css={{ overflow: 'hidden', pl: [0, 4], ml: -4, maxWidth: pxToRem(700) }}>
              <Flex css={{ display: ['none', 'flex'], justifyContent: 'space-between', pl: 4 }}>
                <Heading size="2xs" css={{ display: ['none', 'block'] }}>
                  P&K Map
                </Heading>
                <SliderNavigationRectangle
                  slidePrev={() => swiper?.slidePrev()}
                  slideNext={() => swiper?.slideNext()}
                  cssBox={{
                    w: 6,
                    h: 6,
                  }}
                  cssArrow={{
                    width: pxToRem(10),
                  }}
                />
              </Flex>
              <SliderWrapper
                initialSlide={activeSlide}
                onSwiper={setSwiper}
                onSlideChange={({ realIndex }) => setActiveSlide(realIndex)}
                slidesPerView="auto"
                spaceBetween={16}
                slideToClickedSlide
                loop
                observer
                observeParents
                breakpoints={{
                  768: {
                    slidesPerView: 3.5,
                  },
                  1024: {
                    slidesPerView: 4.5,
                  },
                }}
                css={{
                  '& .swiper': {
                    py: 4,
                    px: [10, 4],
                  },
                  '& .swiper-slide': {
                    cursor: 'pointer',
                    width: 'auto',
                    transform: TRANSITIONS.standard,
                    opacity: [0.3, 1],
                    position: 'relative',
                    pb: [1, 0],
                  },
                  '& .swiper-slide-active': {
                    opacity: 1,
                    borderBottom: ['2px solid white', 'none'],
                    '& img': {
                      boxShadow: '0px 0px 8px 4px #FF00E5',
                    },
                  },
                }}
              >
                {sliderItems.map(({ id, title, imgPrefix }) => (
                  <SwiperSlide key={id}>
                    <Box>
                      <Box display={['none', 'block']}>
                        <Image
                          src={`/static/play_and_kollect/slider/slider_${imgPrefix || id}.jpg`}
                          alt={title}
                        />
                      </Box>
                      <Box display={['block', 'none']}>
                        <Heading size="2xs">{title}</Heading>
                      </Box>
                    </Box>
                  </SwiperSlide>
                ))}
                <Box display={['block', 'none']}>
                  <SliderNavigationRounded />
                </Box>
              </SliderWrapper>
            </Box>
          </Grid>
        </Container>
        <motion.div
          key={activeSlide}
          style={{
            position: 'absolute',
            top: isMobile ? pxToRem(90) : 0,
            left: 0,
            width: '100%',
            height: isMobile ? pxToRem(200) : '100%',
            background: `url(/static/play_and_kollect/slider/bg_${
              sliderItems[activeSlide].imgPrefix || sliderItems[activeSlide].id
            }.jpg) center/cover no-repeat`,
          }}
          initial={{
            opacity: 0,
          }}
          animate={{ opacity: 0.25 }}
          exit={{ opacity: 0 }}
        />
        <GradientShadowOverlay top height={[pxToRem(140), pxToRem(270)]} />
        <GradientShadowOverlay
          css={{ height: [pxToRem(100), pxToRem(270)], top: [pxToRem(210), 'auto'] }}
        />
      </Box>
    </AnimatePresence>
  );
}
